import './shotreview.css';
import type { GameSummary } from '../net/protocol';
import { t } from './i18n';

/**
 * Personal game-history list: the player's recent recorded games (newest first).
 * Selecting one asks the App to fetch its full shot log and open the review panel.
 * Opened from the main menu.
 */
export interface HistoryPanelCallbacks {
  onOpen(id: string): void;
  onClose(): void;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

const MODE_KEY: Record<string, string> = {
  online: 'history.modeOnline',
  ai: 'history.modeAi',
  solo: 'history.modeSolo',
};

export class HistoryPanel {
  private readonly panel = el('div', 'hud-panel interactive');
  private readonly title = el('div', 'review-title');
  private readonly list = el('div', 'review-list history-list');

  constructor(root: HTMLElement, private cb: HistoryPanelCallbacks) {
    this.panel.id = 'history-panel';
    this.panel.style.display = 'none';

    const header = el('div', 'review-header');
    const close = el('button', 'review-close');
    close.textContent = t('mainmenu.back');
    close.onclick = () => {
      this.hide();
      this.cb.onClose();
    };
    this.title.textContent = t('history.title');
    header.append(this.title, close);
    this.panel.append(header, this.list);
    root.appendChild(this.panel);
  }

  get isOpen(): boolean {
    return this.panel.style.display !== 'none';
  }

  open(games: GameSummary[]): void {
    this.title.textContent = t('history.title');
    this.list.replaceChildren();
    if (!games.length) {
      const empty = el('div', 'review-empty');
      empty.textContent = t('history.empty');
      this.list.appendChild(empty);
    }
    for (const g of games) {
      const row = el('div', 'history-row');
      const main = el('div', 'history-main');
      const line1 = el('div', 'review-rowtitle');
      line1.textContent = `${t('variant.' + g.variant)} · ${t(MODE_KEY[g.mode] ?? 'history.modeSolo')}`;
      const line2 = el('div', 'history-sub');
      const when = new Date(g.ts).toLocaleString();
      const opp = g.opponents.filter(Boolean).join(', ');
      line2.textContent = `${when} · ${t('history.shots', { n: g.shotCount })}${opp ? ' · ' + opp : ''}${g.winner ? ' · ' + t('history.won', { name: g.winner }) : ''}`;
      main.append(line1, line2);
      const openBtn = el('button', 'review-action');
      openBtn.textContent = t('history.review');
      openBtn.onclick = () => this.cb.onOpen(g.id);
      row.append(main, openBtn);
      this.list.appendChild(row);
    }
    this.panel.style.display = 'flex';
  }

  hide(): void {
    this.panel.style.display = 'none';
  }
}
